import { useInfiniteQuery, useMutation, useQuery } from 'react-query';

import { CLAIM_QUERY_KEY } from '~/api/query-keys';
import { claimRepository } from '~/api/repository/claim';
import type { CancelProductOrdersRequest, ClaimDetail } from '~/api/model';

// Response 타입 (model에서 재노출)
export type {
  AllCancelType,
  CancellableProductOrder,
  CancelProductOrdersRequest,
  CancelReason,
  ClaimDetail,
  ClaimHistoryPage,
  ClaimReasonItem,
  ClaimReasonType,
  ClaimRefundInfo,
  ClaimSolution,
  ClaimSolutionType,
  CreateClaimRequest,
  OrderCancelPoint,
  OrderClaim,
  OrderClaimRefund,
} from '~/api/model';

/** 교환/반품 목록 (무한 스크롤) */
// GET /api/market-place/v1/claim
export const useInfiniteClaims = () => {
  const { queryKey, queryFn } = CLAIM_QUERY_KEY.histories();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery(queryKey, queryFn, {
      suspense: true,
      getNextPageParam: (lastPage) => {
        if (!lastPage.hasNextPage) return undefined;
        return lastPage.lastId;
      },
    });

  const pages = data?.pages?.reduce<ClaimDetail[]>(
    (acc, page) => acc.concat(page.content),
    [],
  );

  return {
    pages,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
};

/** 취소 가능 상품 조회 */
// GET /api/market-place/v2/orders/{orderId}/cancellable-product-orders
export const useCancellableProductOrders = (orderId: number) => {
  const { queryKey, queryFn } = CLAIM_QUERY_KEY.cancellable(orderId);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!orderId && !Number.isNaN(orderId),
  });
};

/** 취소 완료 상품 상세 조회 */
// GET /api/market-place/v1/claim/cancellation/{productOrderId}/detail
export const useCanceledProductDetail = (productOrderId: number) => {
  const { queryKey, queryFn } = CLAIM_QUERY_KEY.canceledDetail(productOrderId);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!productOrderId && !Number.isNaN(productOrderId),
  });
};

/** 주문 취소 요청 (KPN) */
// POST /api/market-place/v2/claim/kpn/cancel-product-orders
export const useCancelProductOrders = () => {
  return useMutation(
    (request: CancelProductOrdersRequest) =>
      claimRepository.cancelProductOrders(request),
    {
      retryDelay: (failCount) => failCount * 2000,
      retry: (failCount) => failCount < 3,
    },
  );
};

/** 교환/반품 가능 상품 조회 */
// GET /api/market-place/v1/orders/{vendorOrderId}/claimable-product-orders
export const useClaimableProductOrders = (vendorOrderId: number) => {
  const { queryKey, queryFn } = CLAIM_QUERY_KEY.claimable(vendorOrderId);

  return useQuery(queryKey, queryFn, {
    enabled: !!vendorOrderId && !Number.isNaN(vendorOrderId),
  });
};

/** 교환/반품 사유 목록 조회 */
// GET /api/market-place/v1/claim/reason
export const useClaimReasons = () => {
  const { queryKey, queryFn } = CLAIM_QUERY_KEY.reasons();

  return useQuery(queryKey, queryFn, {
    refetchOnWindowFocus: false,
  });
};

/** 교환/반품 상세 조회 */
// GET /api/market-place/v1/claim/{claimId}/detail
export const useClaimDetail = (claimId: number) => {
  const { queryKey, queryFn } = CLAIM_QUERY_KEY.detail(claimId);

  return useQuery(queryKey, queryFn, {
    suspense: true,
    enabled: !!claimId && !Number.isNaN(claimId),
  });
};

/** 교환/반품 요청 생성 (multipart) */
// POST /api/market-place/v1/claim
export const useCreateClaim = () => {
  return useMutation((formData: FormData) =>
    claimRepository.createClaim(formData),
  );
};
